import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Bell, Send, Mail, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { remindersAPI, complianceAPI } from '../../lib/api';

const fmtDate = (iso) => {
  if (!iso) return 'N/A';
  try { return new Date(iso).toLocaleDateString('en-AU'); } catch { return iso; }
};

// Reminder generation + review-before-send workflow.
// Lives inside the Compliance Dashboard ("Reminders" view) and loads its own data.
export default function RemindersPanel() {
  const [deadlines, setDeadlines] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);   // the reminder currently being reviewed
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [d, r] = await Promise.all([
        complianceAPI.listDeadlines(),
        remindersAPI.list(),
      ]);
      setDeadlines(d.data || []);
      setReminders(r.data.reminders || []);
    } catch (err) {
      if (err.response?.status === 403) toast.error('Reminders are restricted to firm staff');
      else toast.error('Failed to load reminders');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (deadline) => {
    try {
      const res = await remindersAPI.generate(deadline.id);
      setDraft(res.data);          // open the review dialog with the generated draft
      toast.success('Draft created — review it before sending');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not generate reminder');
    }
  };

  const handleSend = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      // Save any edits first, then send.
      await remindersAPI.edit(draft.id, { subject: draft.subject, body: draft.body });
      await remindersAPI.send(draft.id);
      toast.success(`Reminder sent to ${draft.recipient_email}`);
      setDraft(null);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Reminders are sent <span className="font-medium">automatically 5 days before</span> each due date.
        You can also generate a draft below, review it, and send one manually at any time.
      </p>

      {/* Upcoming deadlines you can remind on */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Upcoming Deadlines</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="animate-pulse h-32 bg-muted rounded" />
          ) : deadlines.length === 0 ? (
            <p className="text-muted-foreground text-sm py-6 text-center">No compliance deadlines yet.</p>
          ) : (
            <div className="space-y-2">
              {deadlines.map((d) => (
                <div key={d.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50" data-testid={`deadline-${d.id}`}>
                  <div className="flex items-center gap-3">
                    <Bell className="w-4 h-4 text-muted-foreground/70" />
                    <div>
                      <p className="font-medium text-sm">{d.client_name || 'Client'} — {d.type}</p>
                      <p className="text-xs text-muted-foreground">Due {fmtDate(d.due_at)}</p>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => handleGenerate(d)} data-testid={`generate-${d.id}`}>
                    <Mail className="w-4 h-4 mr-2" /> Draft Reminder
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sent / draft history */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Reminder History</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">Every reminder sent to a client — automatic and manual — with the recipient and date.</p>
        </CardHeader>
        <CardContent>
          {reminders.length === 0 ? (
            <p className="text-muted-foreground text-sm py-6 text-center">No reminders yet.</p>
          ) : (
            <div className="space-y-2">
              {reminders.map((r) => (
                <div key={r.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`reminder-${r.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm truncate">{r.subject}</p>
                      {r.status === 'sent' && (
                        r.auto
                          ? <Badge className="bg-violet-100 text-violet-700 text-[10px] shrink-0">Auto</Badge>
                          : <Badge className="bg-muted text-muted-foreground text-[10px] shrink-0">Manual</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      To {r.recipient_email || 'no email'} · {r.sent_at ? `sent ${fmtDate(r.sent_at)}` : 'draft'}
                    </p>
                  </div>
                  <Badge className={r.status === 'sent' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                    {r.status}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Review-before-send dialog */}
      <Dialog open={!!draft} onOpenChange={(open) => !open && setDraft(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Review Reminder</DialogTitle></DialogHeader>
          {draft && (
            <div className="space-y-4">
              {!draft.recipient_email && (
                <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                  <AlertCircle className="w-4 h-4" /> This client has no contact email — add one before sending.
                </div>
              )}
              <div className="space-y-2">
                <Label>To</Label>
                <Input value={draft.recipient_email || ''} disabled />
              </div>
              <div className="space-y-2">
                <Label>Subject</Label>
                <Input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} data-testid="draft-subject" />
              </div>
              <div className="space-y-2">
                <Label>Message</Label>
                <Textarea rows={8} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} data-testid="draft-body" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDraft(null)}>Cancel</Button>
            <Button
              className="bg-primary hover:bg-primary/90"
              disabled={saving || !draft?.recipient_email}
              onClick={handleSend}
              data-testid="send-reminder-button"
            >
              <Send className="w-4 h-4 mr-2" /> {saving ? 'Sending…' : 'Send Reminder'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
